// Joins the deterministic event calendar (events.json) to what the player has
// actually learned (permanent.knowledge / journal.json), and works out whether
// they can still physically get to a thing before it happens.
//
// The whole game is "die, learn the timetable, come back and intercept it", so
// the interception arithmetic — how many days until the event, how many days of
// road lie between here and there — has to be something the game does, not
// something the player does on paper beside the keyboard.

import type { GameEvent, JournalEntry, LocationId, LoopState, PermanentState } from "../types"
import { events, getJournalEntryByKnowledgeId, getLocationById, routes } from "./content"
import { isEventPrevented } from "./eventEngine"
import { effectiveTravelDays, getTravelBlockReason, isRouteOpen } from "./travelEngine"

/** The road from where the player stands to somewhere they can actually reach. */
export interface TravelPath {
  /** Total days of road. Zero when the player is already standing there. */
  days: number
  /** Routes to follow in order; the first is the leg to set out on today. */
  routeIds: string[]
  /** Locations passed through in order, ending at the destination. */
  via: LocationId[]
}

/**
 * Shortest road from the player's current location to every location they can
 * actually reach, by Dijkstra over the route graph.
 *
 * Routes are undirected, locked routes are impassable, and closed borders
 * (Phase 4 trust gates) are treated as walls rather than as expensive edges —
 * a route through a realm that will not admit you is not a slow path, it is
 * not a path. Undiscovered locations are excluded so nothing can ever quote a
 * travel time through somewhere the player has not found yet.
 *
 * The full path — not just its length — is kept because the player has to walk
 * it one leg at a time, and being told "four days away" without being told
 * which road leaves town first is not directions.
 */
export function getTravelPaths(
  permanent: PermanentState,
  loop: LoopState,
): Map<LocationId, TravelPath> {
  const known = new Set(permanent.discoveredLocations)
  const best = new Map<LocationId, TravelPath>([
    [loop.currentLocationId, { days: 0, routeIds: [], via: [] }],
  ])
  const settled = new Set<LocationId>()

  for (;;) {
    // Small graph (a few dozen locations), so a linear scan for the nearest
    // unsettled node is cheaper than maintaining a heap.
    let current: LocationId | null = null
    let currentPath: TravelPath | null = null
    for (const [locationId, path] of best) {
      if (!settled.has(locationId) && (currentPath === null || path.days < currentPath.days)) {
        current = locationId
        currentPath = path
      }
    }
    if (current === null || currentPath === null) break
    settled.add(current)

    for (const route of routes) {
      if (!isRouteOpen(route, permanent)) continue
      const neighbor =
        route.fromLocationId === current
          ? route.toLocationId
          : route.toLocationId === current
            ? route.fromLocationId
            : null
      if (neighbor === null) continue
      if (!known.has(neighbor)) continue
      if (getTravelBlockReason(loop, neighbor) !== null) continue

      const candidate = currentPath.days + effectiveTravelDays(route.travelDays, permanent)
      const existing = best.get(neighbor)
      if (existing === undefined || candidate < existing.days) {
        best.set(neighbor, {
          days: candidate,
          routeIds: [...currentPath.routeIds, route.id],
          via: [...currentPath.via, neighbor],
        })
      }
    }
  }

  return best
}

/** The road to one place, or null if there is no open way there from here. */
export function getTravelPathTo(
  permanent: PermanentState,
  loop: LoopState,
  destinationId: LocationId,
): TravelPath | null {
  return getTravelPaths(permanent, loop).get(destinationId) ?? null
}

export type ScheduledEventStatus =
  | "completed" // Already happened this loop.
  | "missed" // Its day has passed without it completing (conditions were never met).
  | "prevented" // A decision this loop stopped it from happening at all.
  | "today"
  | "upcoming"

export interface ScheduledEvent {
  event: GameEvent
  journalEntry: JournalEntry | undefined
  locationName: string
  status: ScheduledEventStatus
  /** Days between now and the event. Negative once it is in the past. */
  daysUntil: number
  /** Shortest road time from where the player is standing, or null if unreachable. */
  travelDays: number | null
  /** Whether the player could still leave now and arrive before it happens. */
  reachableInTime: boolean
  /** Spare days between arriving and the event firing. Null when unreachable. */
  daysToSpare: number | null
  /** True when the player is already standing where this will happen. */
  alreadyThere: boolean
}

/**
 * An event is on the player's schedule once they have learned it — either by
 * living through it in some earlier loop (its `grantsKnowledge` is permanent
 * and survives death, which is precisely the point of the time loop) or by
 * having seen it complete already in this one.
 *
 * Events that grant no knowledge at all (the second beat of a multi-part
 * event, say) are deliberately never listed on their own: the player has no
 * in-fiction way to know about them.
 */
function isKnown(event: GameEvent, permanent: PermanentState, loop: LoopState): boolean {
  if (loop.completedEvents.includes(event.id)) return true
  return (event.grantsKnowledge ?? []).some((id) => permanent.knowledge.includes(id))
}

function statusOf(event: GameEvent, loop: LoopState): ScheduledEventStatus {
  if (loop.completedEvents.includes(event.id)) return "completed"
  if (isEventPrevented(event, loop)) return "prevented"
  if (event.day < loop.currentDay) return "missed"
  if (event.day === loop.currentDay) return "today"
  // Unmet `conditions` are deliberately not treated as "will not happen" here:
  // they can still be met before the day arrives, and calling an event dead
  // early would be worse than calling it late.
  return "upcoming"
}

/** Everything the player knows is coming, oldest day first. */
export function getKnownSchedule(
  permanent: PermanentState,
  loop: LoopState,
): ScheduledEvent[] {
  const travelTimes = getTravelPaths(permanent, loop)

  return events
    .filter((event) => isKnown(event, permanent, loop))
    .map((event): ScheduledEvent => {
      const status = statusOf(event, loop)
      const daysUntil = event.day - loop.currentDay
      const travelDays = travelTimes.get(event.locationId)?.days ?? null
      const daysToSpare =
        travelDays === null ? null : daysUntil - travelDays
      const knowledgeId = (event.grantsKnowledge ?? [])[0]
      return {
        event,
        journalEntry: knowledgeId ? getJournalEntryByKnowledgeId(knowledgeId) : undefined,
        locationName: getLocationById(event.locationId)?.name ?? event.locationId,
        status,
        daysUntil,
        travelDays,
        reachableInTime:
          (status === "upcoming" || status === "today") &&
          daysToSpare !== null &&
          daysToSpare >= 0,
        daysToSpare,
        alreadyThere: event.locationId === loop.currentLocationId,
      }
    })
    .sort((a, b) => a.event.day - b.event.day)
}

/**
 * The day a piece of knowledge is *about*, when it describes a scheduled
 * event. Journal entries state their day in prose ("on the twenty-second
 * day"), which is unsortable and easy to misread; this recovers the number.
 */
export function getEventDayForKnowledge(knowledgeId: string): number | null {
  const event = events.find((candidate) =>
    (candidate.grantsKnowledge ?? []).includes(knowledgeId),
  )
  return event?.day ?? null
}

/** The next thing the player knows about that has not happened yet. */
export function getNextKnownEvent(
  permanent: PermanentState,
  loop: LoopState,
): ScheduledEvent | null {
  return (
    getKnownSchedule(permanent, loop).find(
      (entry) => entry.status === "today" || entry.status === "upcoming",
    ) ?? null
  )
}
